import React, { useEffect, useState } from "react";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import SalesTable from "../SalesTable/SalesTable";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";
import axiosInstance from "../../../Configuration/axiosInstance";
import SuccessDialogue from "../../../Components/Shared/SuccessDialog/SuccessDialog";
import SearchStrip from "../../../Components/SalesOrder/SearchStrip/SearchStrip";

const PickupOrders = () => {
  const [orders, setOrders] = useState([]);
  const [custId, setCustID] = useState();
  const [customers, setCustomers] = useState();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [changed, setDataChanged] = useState(false);
  const [date, setDate] = useState();
  const [count, setCount] = useState();
  const [clearFilters, setClearFilters] = useState(false);
  const [dialogBox, setDialogBox] = useState(false);
  const [msg, setMsg] = useState("");
  const [size, setSize] = useState(20);
  const [searchCriteria, setSearchCriteria] = useState({
    date: null,
    custId: null,
  });
  const [orderNumber, setOrderNumber] = useState();

  // ✅ UPDATED: Status array includes all relevant statuses for pickup orders
  const status = ["Pending", "Assigned", "Completed", "Cancelled"];
  
  // ✅ UPDATED: Table headers focused on pickup orders
  const tableHeaders = [
    "Order Number",
    "Customer Name",
    "Purchase Order No.",
    "Comments",
    "SKU's",
    "Order Date(dd/mm/yyyy)",
    "Order Type", // Will always show "Pickup" for these orders
    "Order Status",
    "Images",
    "Action",
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // ✅ UPDATED: Fetch all orders and filter for pickup orders
        const [ordersResponse, customersResponse] = await Promise.all([
          axiosInstance.get(
            `order?page=${page}&limit=${pageSize}&customer=${searchCriteria.custId}&date=${searchCriteria.date}&orderNumber=${searchCriteria.orderNumber}&status=${status}`
          ),
          axiosInstance.get(`customers/allcustomers`),
        ]);

        // ✅ NEW: Filter orders to show only pickup orders (isPickup: true)
        const pickupOrders = ordersResponse.data.data.filter((order) => {
          return order.isPickup === true;
        });

        console.log(`📦 Found ${pickupOrders.length} pickup orders out of ${ordersResponse.data.data.length} total orders`);

        setOrders(pickupOrders);
        setCount(pickupOrders.length);
        setCustomers(customersResponse.data.data);
      } catch (err) {
        console.error("Error fetching pickup orders:", err);
        setMsg("Failed to fetch pickup orders. Please try again.");
        setDialogBox(true);
        setTimeout(() => setDialogBox(false), 3000);
      }
      setLoading(false);
    };

    fetchInitialData();
  }, [page, pageSize, changed, searchCriteria]);

  const customerOptions = customers?.map((i) => ({
    value: i._id,
    label: i.businessName,
  }));

  const navigate = useNavigate();
  
  const updateOrder = (i) => {
    navigate("/salesOrders/updateOrder", { state: i });
  };

  const deleteOrder = async (i) => {
    try {
      await axiosInstance.delete(`order/${i}`);
      setDataChanged(!changed);
      setMsg("Pickup order deleted successfully");
      setDialogBox(true);
      setTimeout(() => setDialogBox(false), 2000);
    } catch (error) {
      setDialogBox(true);
      setMsg(error.response?.data?.error || "Delete failed");
      setTimeout(() => setDialogBox(false), 3000);
    }
  };

  const handleNext = () => setPage((prev) => prev + 1);
  const handlePrevious = () => setPage((prev) => prev - 1);

  // ✅ NEW: Function to get pickup order statistics
  const getPickupStats = () => {
    const pending = orders.filter(order => order.status === "Pending").length;
    const assigned = orders.filter(order => order.status === "Assigned").length;
    const completed = orders.filter(order => order.status === "Completed").length;
    const cancelled = orders.filter(order => order.status === "Cancelled").length;
    
    return { pending, assigned, completed, cancelled, total: orders.length };
  };

  const stats = getPickupStats();

  return (
    <div>
      <SuccessDialogue show={dialogBox} msg={msg} />
      
      {/* ✅ NEW: Enhanced header with pickup focus */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h2>📦 Customer Pickup Orders</h2>
        <p style={{ color: "#6c757d", marginBottom: "1rem" }}>
          Orders marked for customer pickup from warehouse
        </p>
        
        {/* ✅ NEW: Quick stats display */}
        {!loading && (
          <div style={{ 
            display: "flex", 
            gap: "1rem", 
            marginBottom: "1rem",
            flexWrap: "wrap"
          }}>
            <div style={{ 
              background: "#f8f9fa", 
              padding: "0.5rem 1rem", 
              borderRadius: "8px",
              border: "1px solid #e9ecef"
            }}>
              <strong>Total:</strong> {stats.total}
            </div>
            {stats.pending > 0 && (
              <div style={{ 
                background: "#fff3cd", 
                padding: "0.5rem 1rem", 
                borderRadius: "8px",
                border: "1px solid #ffeaa7"
              }}>
                <strong>Pending:</strong> {stats.pending}
              </div>
            )}
            {stats.assigned > 0 && (
              <div style={{ 
                background: "#d1ecf1", 
                padding: "0.5rem 1rem", 
                borderRadius: "8px",
                border: "1px solid #a6d8e8"
              }}>
                <strong>Ready:</strong> {stats.assigned}
              </div>
            )}
            {stats.completed > 0 && (
              <div style={{ 
                background: "#d4edda", 
                padding: "0.5rem 1rem", 
                borderRadius: "8px",
                border: "1px solid #a3d9a4"
              }}>
                <strong>Completed:</strong> {stats.completed}
              </div>
            )}
          </div>
        )}
      </div>

      <SearchStrip
        size={size}
        setSize={setSize}
        setPage={setPage}
        setDate={setDate}
        setSearchCriteria={setSearchCriteria}
        date={date}
        custId={custId}
        clearFilters={clearFilters}
        setDataChanged={setDataChanged}
        setClearFilters={setClearFilters}
        setCustID={setCustID}
        changed={changed}
        customerOptions={customerOptions}
        searchCriteria={searchCriteria}
        customers={customers}
        setPageSize={setPageSize}
        pageSize={pageSize}
        orderNumber={orderNumber}
        setOrderNumber={setOrderNumber}
      />

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : orders.length === 0 ? (
        // ✅ NEW: Empty state for no pickup orders
        <div style={{
          textAlign: "center",
          padding: "3rem",
          background: "#f8f9fa",
          borderRadius: "8px",
          border: "2px dashed #dee2e6",
          margin: "2rem 0"
        }}>
          <h3 style={{ color: "#6c757d", marginBottom: "1rem" }}>
            📦 No Pickup Orders Found
          </h3>
          <p style={{ color: "#6c757d" }}>
            {searchCriteria.custId || searchCriteria.date || searchCriteria.orderNumber
              ? "No pickup orders match your current search criteria."
              : "There are currently no orders marked for customer pickup."
            }
          </p>
          {(searchCriteria.custId || searchCriteria.date || searchCriteria.orderNumber) && (
            <button 
              onClick={() => {
                setSearchCriteria({ date: null, custId: null });
                setCustID(null);
                setDate(null);
                setOrderNumber(null);
                setClearFilters(true);
              }}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                background: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <SalesTable
          orders={orders}
          setOrders={setOrders}
          updateOrder={updateOrder}
          deleteOrder={deleteOrder}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          page={page}
          pageSize={pageSize}
          count={count}
          tableHeaders={tableHeaders}
        />
      )}
    </div>
  );
};

export default memo(PickupOrders);